import React, { useEffect, useRef, useState } from "react";
import { useContextStateManagement } from "../context/ContextApi";
import { BusinessHoursPicker } from "../onBoardingTemplateForms/BusinessHoursPicker";
import "./AgentReleasesPage.css";

// New "Agent Releases" page - frontend for the backend's newer /api/agents/:practiceId/:direction/*
// version-control system (CONTRACT.md's "Agent Config Version Control" cross-repo project,
// section 9's Admin API). Distinct from both AgentConfigPage.js (prompt-only, one bundle per
// practice) and ToolDefinitionsPage.js (tools-only, one bundle per practice): this backend
// versions ALL seven config parts (instructions, model, voice, speech, behavior, tools, postCall)
// together as one release, per AGENT (practiceId + direction - a practice has one inbound and one
// outbound agent, each with its own independent release history).
//
// Scope: editing the instructions.prompt field (draft -> submit -> publish), release history with
// rollback, a release/live/draft/candidate diff viewer, and a Settings tab for the 5 other
// immediate-persist parts (model, voice, speech, behavior, postCall - "Save changes", R1: no draft
// step, each save creates a new release right away) plus a read-only Tools list. Tools stay
// editable only through the existing Tools/API Integration pages - building a second tool editor
// here would duplicate ToolDefinitionsPage.js's own already-substantial UI for no real benefit;
// this page only needs to show what's actually live.

const AUTOSAVE_DELAY_MS = 1200;

function formatTimestamp(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Only `prompt` is editable in this page; every other instructions field (specialists,
// entryIntent, knowledgeBase) passes through completely untouched from whatever the draft/live
// release already had, so this page can never silently wipe them.
function emptyInstructions() {
  return { prompt: "", entryIntent: null, specialists: [], knowledgeBase: { enabled: false, assistantId: null, host: null, usagePrompt: null, files: [] } };
}

function normalizeInstructionsBody(raw) {
  return { ...emptyInstructions(), ...(raw || {}) };
}

function serializeInstructions(instructions) {
  return JSON.stringify(instructions);
}

// --- Settings parts (model/voice/speech/behavior/postCall) ---------------------------------
// Each normalizer mirrors releaseCompiler.js's own defaults so a field the live release never set
// still binds to a real, typed form control instead of `undefined`. businessHours and ivrMenu ARE
// editable (BehaviorSettingsForm) - their shapes were verified against the real legacy editing UI
// (BusinessHoursPicker.js, PAuthQOnBoardingForm.js's IVR table), since local dev data for both is
// empty everywhere. Fields this page still doesn't expose a control for (attributes/routingRules
// inside tools) stay edited exclusively through the Tools page.

const SETTINGS_PARTS = [
  { key: "model", label: "Model" },
  { key: "voice", label: "Voice" },
  { key: "speech", label: "Speech" },
  { key: "behavior", label: "Behavior" },
  { key: "postCall", label: "Post-call" },
  { key: "tools", label: "Tools" },
];

function normalizeModelPart(raw) {
  const safe = raw || {};
  return {
    modelId: safe.modelId || "",
    provider: safe.provider || "",
    thinkingEnabled: Boolean(safe.thinkingEnabled),
    reasoningEffort: safe.reasoningEffort || "",
    cacheEnabled: Boolean(safe.cacheEnabled),
    cacheTtl: safe.cacheTtl || "",
  };
}

function normalizeVoicePart(raw) {
  const safe = raw || {};
  const settings = safe.settings || {};
  return {
    provider: safe.provider || "",
    voiceId: safe.voiceId || "",
    ttsModel: safe.ttsModel || "",
    settings: {
      speed: settings.speed ?? "",
      stability: settings.stability ?? "",
      similarityBoost: settings.similarityBoost ?? "",
      style: settings.style ?? "",
      useSpeakerBoost: Boolean(settings.useSpeakerBoost),
    },
  };
}

function normalizeSpeechPart(raw) {
  const safe = raw || {};
  return {
    sttProvider: safe.sttProvider || "",
    sttModel: safe.sttModel || "",
    languages: Array.isArray(safe.languages) ? safe.languages : [],
    fluxKeyterms: Array.isArray(safe.fluxKeyterms) ? safe.fluxKeyterms : [],
  };
}

// businessHours shape ({Days, StartTime, EndTime}) and ivrMenu's per-option shape ({digit, label,
// action, instruction}) are verified against the real editing UI in onBoardingTemplateForms
// (BusinessHoursPicker.js, PAuthQOnBoardingForm.js's IVR table) - local dev data for both is empty
// everywhere, so the legacy form code (not sample data) is the real source of truth here.
function normalizeBehaviorPart(raw) {
  const safe = raw || {};
  const businessHours = safe.businessHours || {};
  const ivrMenu = safe.ivrMenu || {};
  return {
    ...safe,
    firstMessage: safe.firstMessage || "",
    lastMessage: safe.lastMessage || "",
    greetingInterruptible: Boolean(safe.greetingInterruptible),
    turnDetectionMode: safe.turnDetectionMode || "",
    eouMinDelay: safe.eouMinDelay ?? "",
    eouMaxDelay: safe.eouMaxDelay ?? "",
    sipTerminationSeconds: safe.sipTerminationSeconds ?? "",
    transferSipTerminationSeconds: safe.transferSipTerminationSeconds ?? "",
    backgroundNoise: safe.backgroundNoise || "",
    voicemail: safe.voicemail || "",
    outboundCallerIdSource: safe.outboundCallerIdSource || "",
    businessHours: {
      Days: Array.isArray(businessHours.Days) ? businessHours.Days : [],
      StartTime: businessHours.StartTime || "",
      EndTime: businessHours.EndTime || "",
    },
    ivrMenu: {
      enabled: Boolean(ivrMenu.enabled),
      options: Array.isArray(ivrMenu.options)
        ? ivrMenu.options.map((option) => ({
            digit: option?.digit || "",
            label: option?.label || "",
            action: option?.action === "transfer" ? "transfer" : "agent",
            instruction: option?.instruction || "",
          }))
        : [],
    },
  };
}

function normalizePostCallPart(raw) {
  const safe = raw || {};
  return {
    // Real production data uses {name, description} per entry (verified against
    // InboundCallsOnboarding.SummaryParameters), not an arbitrary shape - releaseCompiler.js's own
    // normalizer passes this array through as-is without renaming fields.
    summaryParameters: Array.isArray(safe.summaryParameters)
      ? safe.summaryParameters.map((p) => ({ name: p?.name || "", description: p?.description || "" }))
      : [],
  };
}

const PART_NORMALIZERS = {
  model: normalizeModelPart,
  voice: normalizeVoicePart,
  speech: normalizeSpeechPart,
  behavior: normalizeBehaviorPart,
  postCall: normalizePostCallPart,
};

// Converts a form-bound draft back to the exact shape the backend expects - numeric fields the
// form keeps as strings ("" for unset) become real numbers or null, never an empty string.
function toNumberOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function buildModelPartValue(draft) {
  return {
    modelId: draft.modelId || null,
    thinkingEnabled: draft.thinkingEnabled,
    reasoningEffort: draft.reasoningEffort || null,
    cacheEnabled: draft.cacheEnabled,
    cacheTtl: draft.cacheTtl || null,
  };
}

function buildVoicePartValue(draft) {
  return {
    provider: draft.provider || null,
    voiceId: draft.voiceId || null,
    ttsModel: draft.ttsModel || null,
    settings: {
      speed: toNumberOrNull(draft.settings.speed),
      stability: toNumberOrNull(draft.settings.stability),
      similarityBoost: toNumberOrNull(draft.settings.similarityBoost),
      style: toNumberOrNull(draft.settings.style),
      useSpeakerBoost: draft.settings.useSpeakerBoost,
    },
  };
}

function buildSpeechPartValue(draft) {
  return {
    sttProvider: draft.sttProvider || null,
    sttModel: draft.sttModel || null,
    languages: draft.languages,
    fluxKeyterms: draft.fluxKeyterms,
  };
}

function buildBehaviorPartValue(draft) {
  return {
    ...draft,
    eouMinDelay: toNumberOrNull(draft.eouMinDelay),
    eouMaxDelay: toNumberOrNull(draft.eouMaxDelay),
    sipTerminationSeconds: toNumberOrNull(draft.sipTerminationSeconds),
    transferSipTerminationSeconds: toNumberOrNull(draft.transferSipTerminationSeconds),
  };
}

function buildPostCallPartValue(draft) {
  return { summaryParameters: draft.summaryParameters };
}

const PART_VALUE_BUILDERS = {
  model: buildModelPartValue,
  voice: buildVoicePartValue,
  speech: buildSpeechPartValue,
  behavior: buildBehaviorPartValue,
  postCall: buildPostCallPartValue,
};

function parseCommaList(text) {
  return text
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

// Short, human label for a diff-pickable item - either a real release or one of the three
// special "live"/"draft"/"candidate" values the backend's diff endpoint also accepts.
function releaseOptionLabel(release) {
  const when = formatTimestamp(release.createdAt);
  return `${release.label} - ${release.origin}${when ? ` - ${when}` : ""}`;
}

function DiffPartPath({ change }) {
  if (change.credentialChanged) {
    return (
      <div className="agent-releases-diff-row" key={change.path}>
        <span className="agent-releases-diff-path">{change.path}</span>
        <span className="agent-releases-diff-credential">credential changed</span>
      </div>
    );
  }
  const before = change.before === undefined ? null : JSON.stringify(change.before);
  const after = change.after === undefined ? null : JSON.stringify(change.after);
  return (
    <div className="agent-releases-diff-row" key={change.path}>
      <span className="agent-releases-diff-path">{change.path}</span>
      <span className={`agent-releases-diff-op agent-releases-diff-op--${change.op}`}>{change.op}</span>
      <div className="agent-releases-diff-values">
        {before !== null ? <div className="agent-releases-diff-old">- {before}</div> : null}
        {after !== null ? <div className="agent-releases-diff-new">+ {after}</div> : null}
      </div>
    </div>
  );
}

function DiffPromptLines({ lines }) {
  return (
    <div className="agent-releases-diff-prompt">
      {lines.map((line, index) => (
        <div
          key={index}
          className={`agent-releases-diff-prompt-line agent-releases-diff-prompt-line--${line.type}`}
        >
          {line.type === "add" ? "+ " : line.type === "remove" ? "- " : "  "}
          {line.line}
        </div>
      ))}
    </div>
  );
}

function ModelSettingsForm({ value, onChange }) {
  return (
    <>
      <label className="agent-releases-label" htmlFor="settingsModelId">
        Model ID
      </label>
      <input
        id="settingsModelId"
        type="text"
        className="agent-releases-input"
        value={value.modelId}
        onChange={(event) => onChange({ ...value, modelId: event.target.value })}
        placeholder="us.anthropic.claude-sonnet-5-5"
      />

      <div className="agent-releases-field-row">
        <label className="agent-releases-checkbox-label">
          <input
            type="checkbox"
            checked={value.thinkingEnabled}
            onChange={(event) => onChange({ ...value, thinkingEnabled: event.target.checked })}
          />
          Thinking enabled
        </label>
        <label className="agent-releases-checkbox-label">
          <input
            type="checkbox"
            checked={value.cacheEnabled}
            onChange={(event) => onChange({ ...value, cacheEnabled: event.target.checked })}
          />
          Prompt caching enabled
        </label>
      </div>

      <div className="agent-releases-field-row">
        <div className="agent-releases-field-col">
          <label className="agent-releases-label" htmlFor="settingsReasoningEffort">
            Reasoning effort
          </label>
          <select
            id="settingsReasoningEffort"
            className="agent-releases-input"
            value={value.reasoningEffort}
            onChange={(event) => onChange({ ...value, reasoningEffort: event.target.value })}
          >
            <option value="">(none)</option>
            <option value="low">low</option>
            <option value="medium">medium</option>
            <option value="high">high</option>
            <option value="xhigh">xhigh</option>
          </select>
        </div>
        <div className="agent-releases-field-col">
          <label className="agent-releases-label" htmlFor="settingsCacheTtl">
            Cache TTL
          </label>
          <input
            id="settingsCacheTtl"
            type="text"
            className="agent-releases-input"
            value={value.cacheTtl}
            onChange={(event) => onChange({ ...value, cacheTtl: event.target.value })}
            placeholder="5m"
          />
        </div>
      </div>
    </>
  );
}

function VoiceSettingsForm({ value, onChange }) {
  const setSetting = (key, settingValue) =>
    onChange({ ...value, settings: { ...value.settings, [key]: settingValue } });

  return (
    <>
      <div className="agent-releases-field-row">
        <div className="agent-releases-field-col">
          <label className="agent-releases-label" htmlFor="settingsVoiceProvider">
            Provider
          </label>
          <input
            id="settingsVoiceProvider"
            type="text"
            className="agent-releases-input"
            value={value.provider}
            onChange={(event) => onChange({ ...value, provider: event.target.value })}
            placeholder="elevenlabs"
          />
        </div>
        <div className="agent-releases-field-col">
          <label className="agent-releases-label" htmlFor="settingsVoiceId">
            Voice ID
          </label>
          <input
            id="settingsVoiceId"
            type="text"
            className="agent-releases-input"
            value={value.voiceId}
            onChange={(event) => onChange({ ...value, voiceId: event.target.value })}
          />
        </div>
      </div>

      <label className="agent-releases-label agent-releases-label--spaced" htmlFor="settingsTtsModel">
        TTS model
      </label>
      <input
        id="settingsTtsModel"
        type="text"
        className="agent-releases-input"
        value={value.ttsModel}
        onChange={(event) => onChange({ ...value, ttsModel: event.target.value })}
        placeholder="eleven_flash_v2_5"
      />

      <div className="agent-releases-field-row">
        {["speed", "stability", "similarityBoost", "style"].map((key) => (
          <div className="agent-releases-field-col" key={key}>
            <label className="agent-releases-label" htmlFor={`settingsVoice-${key}`}>
              {key}
            </label>
            <input
              id={`settingsVoice-${key}`}
              type="number"
              step="0.05"
              className="agent-releases-input"
              value={value.settings[key]}
              onChange={(event) => setSetting(key, event.target.value)}
            />
          </div>
        ))}
      </div>

      <label className="agent-releases-checkbox-label agent-releases-label--spaced">
        <input
          type="checkbox"
          checked={value.settings.useSpeakerBoost}
          onChange={(event) => setSetting("useSpeakerBoost", event.target.checked)}
        />
        Speaker boost
      </label>
    </>
  );
}

function SpeechSettingsForm({ value, onChange }) {
  return (
    <>
      <div className="agent-releases-field-row">
        <div className="agent-releases-field-col">
          <label className="agent-releases-label" htmlFor="settingsSttProvider">
            STT provider
          </label>
          <input
            id="settingsSttProvider"
            type="text"
            className="agent-releases-input"
            value={value.sttProvider}
            onChange={(event) => onChange({ ...value, sttProvider: event.target.value })}
            placeholder="deepgram"
          />
        </div>
        <div className="agent-releases-field-col">
          <label className="agent-releases-label" htmlFor="settingsSttModel">
            STT model
          </label>
          <input
            id="settingsSttModel"
            type="text"
            className="agent-releases-input"
            value={value.sttModel}
            onChange={(event) => onChange({ ...value, sttModel: event.target.value })}
            placeholder="flux-general-en"
          />
        </div>
      </div>

      <label className="agent-releases-label agent-releases-label--spaced" htmlFor="settingsLanguages">
        Languages (comma-separated)
      </label>
      <input
        id="settingsLanguages"
        type="text"
        className="agent-releases-input"
        value={value.languages.join(", ")}
        onChange={(event) => onChange({ ...value, languages: parseCommaList(event.target.value) })}
        placeholder="en"
      />

      <label className="agent-releases-label agent-releases-label--spaced" htmlFor="settingsFluxKeyterms">
        Flux keyterms (comma-separated)
      </label>
      <input
        id="settingsFluxKeyterms"
        type="text"
        className="agent-releases-input"
        value={value.fluxKeyterms.join(", ")}
        onChange={(event) => onChange({ ...value, fluxKeyterms: parseCommaList(event.target.value) })}
      />
    </>
  );
}

function BehaviorSettingsForm({ value, onChange, direction }) {
  return (
    <>
      <label className="agent-releases-label" htmlFor="settingsFirstMessage">
        First message
      </label>
      <textarea
        id="settingsFirstMessage"
        className="agent-releases-textarea"
        style={{ minHeight: 70 }}
        value={value.firstMessage}
        onChange={(event) => onChange({ ...value, firstMessage: event.target.value })}
      />

      <label className="agent-releases-label agent-releases-label--spaced" htmlFor="settingsLastMessage">
        Last message
      </label>
      <textarea
        id="settingsLastMessage"
        className="agent-releases-textarea"
        style={{ minHeight: 70 }}
        value={value.lastMessage}
        onChange={(event) => onChange({ ...value, lastMessage: event.target.value })}
      />

      <label className="agent-releases-checkbox-label agent-releases-label--spaced">
        <input
          type="checkbox"
          checked={value.greetingInterruptible}
          onChange={(event) => onChange({ ...value, greetingInterruptible: event.target.checked })}
        />
        Greeting interruptible
      </label>

      <div className="agent-releases-field-row">
        <div className="agent-releases-field-col">
          <label className="agent-releases-label" htmlFor="settingsTurnDetectionMode">
            Turn detection mode
          </label>
          <input
            id="settingsTurnDetectionMode"
            type="text"
            className="agent-releases-input"
            value={value.turnDetectionMode}
            onChange={(event) => onChange({ ...value, turnDetectionMode: event.target.value })}
            placeholder="semantic"
          />
        </div>
        <div className="agent-releases-field-col">
          <label className="agent-releases-label" htmlFor="settingsBackgroundNoise">
            Background noise
          </label>
          <input
            id="settingsBackgroundNoise"
            type="text"
            className="agent-releases-input"
            value={value.backgroundNoise}
            onChange={(event) => onChange({ ...value, backgroundNoise: event.target.value })}
          />
        </div>
      </div>

      <div className="agent-releases-field-row">
        <div className="agent-releases-field-col">
          <label className="agent-releases-label" htmlFor="settingsEouMin">
            EOU min delay (sec)
          </label>
          <input
            id="settingsEouMin"
            type="number"
            step="0.1"
            className="agent-releases-input"
            value={value.eouMinDelay}
            onChange={(event) => onChange({ ...value, eouMinDelay: event.target.value })}
          />
        </div>
        <div className="agent-releases-field-col">
          <label className="agent-releases-label" htmlFor="settingsEouMax">
            EOU max delay (sec)
          </label>
          <input
            id="settingsEouMax"
            type="number"
            step="0.1"
            className="agent-releases-input"
            value={value.eouMaxDelay}
            onChange={(event) => onChange({ ...value, eouMaxDelay: event.target.value })}
          />
        </div>
      </div>

      <div className="agent-releases-field-row">
        <div className="agent-releases-field-col">
          <label className="agent-releases-label" htmlFor="settingsSipTermination">
            SIP termination (sec)
          </label>
          <input
            id="settingsSipTermination"
            type="number"
            className="agent-releases-input"
            value={value.sipTerminationSeconds}
            onChange={(event) => onChange({ ...value, sipTerminationSeconds: event.target.value })}
          />
        </div>
        <div className="agent-releases-field-col">
          <label className="agent-releases-label" htmlFor="settingsTransferSipTermination">
            Transfer SIP termination (sec)
          </label>
          <input
            id="settingsTransferSipTermination"
            type="number"
            className="agent-releases-input"
            value={value.transferSipTerminationSeconds}
            onChange={(event) => onChange({ ...value, transferSipTerminationSeconds: event.target.value })}
          />
        </div>
      </div>

      {direction === "outbound" ? (
        <div className="agent-releases-field-row">
          <div className="agent-releases-field-col">
            <label className="agent-releases-label" htmlFor="settingsVoicemail">
              Voicemail
            </label>
            <input
              id="settingsVoicemail"
              type="text"
              className="agent-releases-input"
              value={value.voicemail}
              onChange={(event) => onChange({ ...value, voicemail: event.target.value })}
            />
          </div>
          <div className="agent-releases-field-col">
            <label className="agent-releases-label" htmlFor="settingsOutboundCallerIdSource">
              Outbound caller ID source
            </label>
            <input
              id="settingsOutboundCallerIdSource"
              type="text"
              className="agent-releases-input"
              value={value.outboundCallerIdSource}
              onChange={(event) => onChange({ ...value, outboundCallerIdSource: event.target.value })}
            />
          </div>
        </div>
      ) : null}

      <div className="agent-releases-subsection">
        <BusinessHoursPicker
          days={value.businessHours.Days}
          onDaysChange={(days) => onChange({ ...value, businessHours: { ...value.businessHours, Days: days } })}
          startTime={value.businessHours.StartTime}
          onStartTimeChange={(time) =>
            onChange({ ...value, businessHours: { ...value.businessHours, StartTime: time } })
          }
          endTime={value.businessHours.EndTime}
          onEndTimeChange={(time) => onChange({ ...value, businessHours: { ...value.businessHours, EndTime: time } })}
        />
      </div>

      {direction === "inbound" ? (
        <div className="agent-releases-subsection">
          <label className="agent-releases-checkbox-label">
            <input
              type="checkbox"
              checked={value.ivrMenu.enabled}
              onChange={(event) =>
                onChange({
                  ...value,
                  ivrMenu: {
                    enabled: event.target.checked,
                    options: event.target.checked ? value.ivrMenu.options : [],
                  },
                })
              }
            />
            IVR menu enabled
          </label>

          {value.ivrMenu.enabled ? (
            <>
              {value.ivrMenu.options.map((option, index) => {
                const updateOption = (field, fieldValue) =>
                  onChange({
                    ...value,
                    ivrMenu: {
                      ...value.ivrMenu,
                      options: value.ivrMenu.options.map((o, i) => (i === index ? { ...o, [field]: fieldValue } : o)),
                    },
                  });
                const removeOption = () =>
                  onChange({
                    ...value,
                    ivrMenu: { ...value.ivrMenu, options: value.ivrMenu.options.filter((_, i) => i !== index) },
                  });
                return (
                  <div className="agent-releases-ivr-row" key={index}>
                    <input
                      type="text"
                      maxLength={1}
                      className="agent-releases-input agent-releases-ivr-digit"
                      placeholder="1"
                      value={option.digit}
                      onChange={(event) => updateOption("digit", event.target.value)}
                    />
                    <input
                      type="text"
                      className="agent-releases-input agent-releases-ivr-label"
                      placeholder="e.g. Billing questions"
                      value={option.label}
                      onChange={(event) => updateOption("label", event.target.value)}
                    />
                    <select
                      className="agent-releases-input agent-releases-ivr-action"
                      value={option.action}
                      onChange={(event) => updateOption("action", event.target.value)}
                    >
                      <option value="agent">Agent handles</option>
                      <option value="transfer">Transfer</option>
                    </select>
                    <input
                      type="text"
                      className="agent-releases-input agent-releases-ivr-instruction"
                      placeholder="e.g. Ask the caller for their name and DOB"
                      value={option.instruction}
                      onChange={(event) => updateOption("instruction", event.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-link agent-releases-inline-action agent-releases-inline-action--danger"
                      onClick={removeOption}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                className="btn btn-link agent-releases-inline-action"
                onClick={() =>
                  onChange({
                    ...value,
                    ivrMenu: {
                      ...value.ivrMenu,
                      options: [...value.ivrMenu.options, { digit: "", label: "", action: "agent", instruction: "" }],
                    },
                  })
                }
              >
                + Add IVR option
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      <div className="agent-releases-note" style={{ textAlign: "left", marginTop: "0.75rem" }}>
        Saving writes every field above exactly as shown, including business hours and the IVR
        menu.
      </div>
    </>
  );
}

function PostCallSettingsForm({ value, onChange }) {
  const updateParam = (index, field, fieldValue) => {
    onChange({
      ...value,
      summaryParameters: value.summaryParameters.map((param, i) =>
        i === index ? { ...param, [field]: fieldValue } : param,
      ),
    });
  };
  const removeParam = (index) => {
    onChange({ ...value, summaryParameters: value.summaryParameters.filter((_, i) => i !== index) });
  };
  const addParam = () => {
    onChange({ ...value, summaryParameters: [...value.summaryParameters, { name: "", description: "" }] });
  };

  return (
    <>
      <div className="agent-releases-subsection-header">
        <span className="agent-releases-label">Summary parameters</span>
        <button type="button" className="btn btn-link agent-releases-inline-action" onClick={addParam}>
          + Add parameter
        </button>
      </div>
      {value.summaryParameters.length === 0 ? (
        <div className="agent-releases-empty">No post-call summary parameters configured.</div>
      ) : null}
      {value.summaryParameters.map((param, index) => (
        <div className="agent-releases-postcall-row" key={index}>
          <input
            type="text"
            className="agent-releases-input agent-releases-postcall-name"
            placeholder="name"
            value={param.name}
            onChange={(event) => updateParam(index, "name", event.target.value)}
          />
          <textarea
            className="agent-releases-textarea agent-releases-postcall-description"
            placeholder="description / instructions for this summary field"
            value={param.description}
            onChange={(event) => updateParam(index, "description", event.target.value)}
          />
          <button
            type="button"
            className="btn btn-link agent-releases-inline-action agent-releases-inline-action--danger"
            onClick={() => removeParam(index)}
          >
            Remove
          </button>
        </div>
      ))}
    </>
  );
}

function ToolsReadOnlyList({ tools }) {
  if (!tools || tools.length === 0) {
    return <div className="agent-releases-empty">No tools in this agent&apos;s live release.</div>;
  }
  return (
    <div className="table-responsive">
      <table className="table align-middle agent-releases-tools-table">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Type</th>
            <th scope="col">Summary</th>
          </tr>
        </thead>
        <tbody>
          {tools.map((tool) => (
            <tr key={tool.key}>
              <td>{tool.name}</td>
              <td>{tool.functionType}</td>
              <td className="agent-releases-tools-summary-cell">
                {tool.functionType === "apiFunction"
                  ? tool.definition?.url || "(no URL set)"
                  : tool.definition?.handoverPhoneNumber || "(no number set)"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="agent-releases-note" style={{ textAlign: "left" }}>
        Tools are edited on the Tools page - this is a read-only view of what&apos;s in this
        agent&apos;s live release.
      </div>
    </div>
  );
}

export const AgentReleasesPage = () => {
  const {
    PracticeId,
    getAgentSummary,
    getAgentDraft,
    saveAgentDraft,
    submitAgentDraft,
    publishAgentDraft,
    saveAgentPart,
    rollbackAgentRelease,
    getAgentReleases,
    getAgentRelease,
    getAgentDeployments,
    getAgentDiff,
  } = useContextStateManagement();

  const [direction, setDirection] = useState("inbound");
  const [activePageTab, setActivePageTab] = useState("editor");

  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState("");

  const [agentSummary, setAgentSummary] = useState(null);
  const [instructions, setInstructions] = useState(emptyInstructions());
  const [draftRevision, setDraftRevision] = useState(null);
  const [draftSubmitted, setDraftSubmitted] = useState(false);

  const [saveState, setSaveState] = useState("idle");
  const [saveErrorMessage, setSaveErrorMessage] = useState("");
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const [submitState, setSubmitState] = useState("idle");
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");

  const [publishState, setPublishState] = useState("idle");
  const [publishMessage, setPublishMessage] = useState("");

  const [releases, setReleases] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErrorMessage, setHistoryErrorMessage] = useState("");
  const [rollbackTargetRelease, setRollbackTargetRelease] = useState(null);
  const [rollbackReason, setRollbackReason] = useState("");
  const [rollbackState, setRollbackState] = useState("idle");
  const [rollbackErrorMessage, setRollbackErrorMessage] = useState("");

  const [deployments, setDeployments] = useState([]);

  const [diffFrom, setDiffFrom] = useState("live");
  const [diffTo, setDiffTo] = useState("draft");
  const [diffResult, setDiffResult] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffErrorMessage, setDiffErrorMessage] = useState("");

  // Settings tab (model/voice/speech/behavior/postCall/tools) - immediate-persist "Save changes"
  // (R1), no draft step. partDrafts holds the working copy of whichever part is open; liveParts is
  // the full 7-part document of the current live release, refreshed after every successful save.
  const [settingsPartName, setSettingsPartName] = useState("model");
  const [liveParts, setLiveParts] = useState(null);
  const [partDrafts, setPartDrafts] = useState({});
  const [partSaveState, setPartSaveState] = useState("idle");
  const [partErrorMessage, setPartErrorMessage] = useState("");

  const lastSavedSnapshotRef = useRef(serializeInstructions(emptyInstructions()));
  const debounceRef = useRef(null);
  const saveStateRef = useRef("idle");
  const instructionsRef = useRef(emptyInstructions());

  useEffect(() => {
    saveStateRef.current = saveState;
  }, [saveState]);

  useEffect(() => {
    instructionsRef.current = instructions;
  }, [instructions]);

  const refreshSummary = async () => {
    if (!PracticeId) return null;
    const summary = await getAgentSummary(PracticeId, direction);
    setAgentSummary(summary);
    return summary;
  };

  // Fetches the full 7-part document of whatever release `summary.release` points at, and seeds
  // partDrafts from it for whichever settings part is currently open. Called after every load and
  // after every successful Save changes, so the form always reflects what's actually live.
  const refreshLiveParts = async (summary, openPartName) => {
    if (!PracticeId || !summary?.release) {
      setLiveParts(null);
      return;
    }
    const release = await getAgentRelease(PracticeId, direction, summary.release.id);
    setLiveParts(release.parts);
    const partName = openPartName || settingsPartName;
    if (PART_NORMALIZERS[partName]) {
      setPartDrafts((current) => ({
        ...current,
        [partName]: PART_NORMALIZERS[partName](release.parts[partName]),
      }));
    }
  };

  const refreshHistory = async () => {
    if (!PracticeId) return;
    setHistoryLoading(true);
    try {
      const [releasesResponse, deploymentsResponse] = await Promise.all([
        getAgentReleases(PracticeId, direction),
        getAgentDeployments(PracticeId, direction),
      ]);
      setReleases(Array.isArray(releasesResponse?.releases) ? releasesResponse.releases : []);
      setDeployments(Array.isArray(deploymentsResponse?.deployments) ? deploymentsResponse.deployments : []);
      setHistoryErrorMessage("");
    } catch (error) {
      setHistoryErrorMessage(error?.message || "Couldn't load release history. Try again.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const persistDraft = async (currentInstructions) => {
    if (!PracticeId) return;
    setSaveState("saving");
    try {
      const response = await saveAgentDraft(PracticeId, direction, currentInstructions, draftRevision);
      lastSavedSnapshotRef.current = serializeInstructions(currentInstructions);
      setDraftRevision(response.revision);
      setDraftSubmitted(Boolean(response.submitted));
      setSaveState("saved");
      setLastSavedAt(response.updatedAt || new Date().toISOString());
      setSaveErrorMessage("");
    } catch (error) {
      setSaveState("error");
      setSaveErrorMessage(
        error?.status === 409
          ? "Someone else saved a change to this draft - reload the page to see the latest version."
          : error?.message || "Couldn't save the draft. Your changes are kept locally - try again.",
      );
    }
  };

  // Load agent summary + draft whenever the practice or direction changes.
  useEffect(() => {
    if (!PracticeId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setInitialLoadDone(false);
    setLoadErrorMessage("");
    setActivePageTab("editor");

    setLiveParts(null);
    setPartDrafts({});

    Promise.all([getAgentSummary(PracticeId, direction), getAgentDraft(PracticeId, direction)])
      .then(async ([summary, draft]) => {
        if (cancelled) return;

        setAgentSummary(summary);

        const startingInstructions = draft?.exists
          ? normalizeInstructionsBody(draft.body)
          : emptyInstructions();
        setInstructions(startingInstructions);
        lastSavedSnapshotRef.current = serializeInstructions(startingInstructions);
        setDraftRevision(draft?.exists ? draft.revision : null);
        setDraftSubmitted(Boolean(draft?.submitted));
        setLastSavedAt(draft?.updatedAt || null);
        setSaveState("idle");

        if (summary?.release) {
          const release = await getAgentRelease(PracticeId, direction, summary.release.id);
          if (cancelled) return;
          setLiveParts(release.parts);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadErrorMessage(error?.message || "Failed to load this agent.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setInitialLoadDone(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PracticeId, direction]);

  // Autosave: debounce edits, only once the initial load has finished.
  useEffect(() => {
    if (!initialLoadDone || !PracticeId) return undefined;
    if (serializeInstructions(instructions) === lastSavedSnapshotRef.current) return undefined;

    setSaveState("unsaved");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      persistDraft(instructions);
    }, AUTOSAVE_DELAY_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructions, initialLoadDone, PracticeId]);

  // Best-effort flush of a pending edit if the user navigates away before the debounce fires.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (saveStateRef.current === "unsaved" || saveStateRef.current === "saving") {
        persistDraft(instructionsRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePromptChange = (value) => {
    setInstructions((current) => ({ ...current, prompt: value }));
  };

  const handleSaveNow = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    persistDraft(instructions);
  };

  const handleSubmitDraft = async () => {
    if (!PracticeId) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    setSubmitState("submitting");
    setSubmitErrorMessage("");
    try {
      // Make sure the latest edit is actually saved before freezing it as a candidate.
      if (serializeInstructions(instructions) !== lastSavedSnapshotRef.current) {
        await persistDraft(instructions);
      }
      await submitAgentDraft(PracticeId, direction);
      setDraftSubmitted(true);
      setPublishMessage("");
      setSubmitState("idle");
    } catch (error) {
      setSubmitState("error");
      setSubmitErrorMessage(error?.message || "Couldn't submit the draft. Try again.");
    }
  };

  const handlePublish = async () => {
    if (!PracticeId || !agentSummary) return;
    setPublishState("publishing");
    setPublishMessage("");
    try {
      const response = await publishAgentDraft(PracticeId, direction, {
        draftRevision,
        expectedRouteRevision: agentSummary.routeRevision,
        reason: "Published from Agent Releases page",
      });
      setPublishMessage(`Published ${response.release.label} as the live release.`);
      setPublishState("idle");
      const newSummary = await refreshSummary();
      await refreshLiveParts(newSummary);
    } catch (error) {
      setPublishState("error");
      setPublishMessage(
        error?.status === 409
          ? "The live release changed since this page loaded - reload before publishing."
          : error?.message || "Couldn't publish. Try again.",
      );
    }
  };

  const handleOpenHistoryTab = () => {
    setActivePageTab("history");
    refreshHistory();
  };

  const handleOpenDiffTab = () => {
    setActivePageTab("diff");
    if (releases.length === 0) refreshHistory();
  };

  const handleOpenSettingsTab = () => {
    setActivePageTab("settings");
    if (!liveParts) refreshLiveParts(agentSummary);
  };

  const handleSelectSettingsPart = (partName) => {
    setSettingsPartName(partName);
    setPartErrorMessage("");
    setPartSaveState("idle");
    if (PART_NORMALIZERS[partName] && liveParts && !partDrafts[partName]) {
      setPartDrafts((current) => ({ ...current, [partName]: PART_NORMALIZERS[partName](liveParts[partName]) }));
    }
  };

  const handlePartFieldChange = (partName, nextValue) => {
    setPartDrafts((current) => ({ ...current, [partName]: nextValue }));
  };

  const handleSavePart = async (partName) => {
    if (!PracticeId || !agentSummary?.release || !partDrafts[partName]) return;
    setPartSaveState("saving");
    setPartErrorMessage("");
    try {
      const value = PART_VALUE_BUILDERS[partName](partDrafts[partName]);
      await saveAgentPart(PracticeId, direction, partName, {
        value,
        expectedRouteRevision: agentSummary.routeRevision,
        reason: `Saved ${partName} settings from Agent Releases page`,
      });
      setPartSaveState("saved");
      const newSummary = await refreshSummary();
      await refreshLiveParts(newSummary, partName);
      await refreshHistory();
    } catch (error) {
      setPartSaveState("error");
      setPartErrorMessage(
        error?.status === 409
          ? "The live release changed since this page loaded - reload before saving."
          : error?.message || `Couldn't save ${partName}. Try again.`,
      );
    }
  };

  const handleRequestRollback = (release) => {
    setRollbackErrorMessage("");
    setRollbackReason("");
    setRollbackTargetRelease(release);
  };

  const handleCancelRollback = () => {
    setRollbackTargetRelease(null);
  };

  const handleConfirmRollback = async () => {
    if (!PracticeId || !rollbackTargetRelease || !agentSummary) return;
    if (!rollbackReason.trim()) {
      setRollbackErrorMessage("A reason is required - it's shown in the deployment history.");
      return;
    }
    setRollbackState("rolling-back");
    setRollbackErrorMessage("");
    try {
      await rollbackAgentRelease(PracticeId, direction, {
        toReleaseId: rollbackTargetRelease.id,
        expectedRouteRevision: agentSummary.routeRevision,
        reason: rollbackReason.trim(),
      });
      setRollbackTargetRelease(null);
      setRollbackState("idle");
      const newSummary = await refreshSummary();
      await Promise.all([refreshHistory(), refreshLiveParts(newSummary)]);
    } catch (error) {
      setRollbackState("error");
      setRollbackErrorMessage(
        error?.status === 409
          ? "The live release changed since this page loaded - reload before rolling back."
          : error?.message || "Couldn't roll back to that release. Try again.",
      );
    }
  };

  const handleRunDiff = async () => {
    if (!PracticeId) return;
    setDiffLoading(true);
    setDiffErrorMessage("");
    setDiffResult(null);
    try {
      const response = await getAgentDiff(PracticeId, direction, diffFrom, diffTo);
      setDiffResult(response);
    } catch (error) {
      setDiffErrorMessage(error?.message || "Couldn't compute that diff. Try again.");
    } finally {
      setDiffLoading(false);
    }
  };

  const savedAtLabel = formatTimestamp(lastSavedAt);
  const statusMeta = {
    idle: { label: "No changes yet", className: "agent-releases-status--idle" },
    unsaved: { label: "Unsaved changes", className: "agent-releases-status--unsaved" },
    saving: { label: "Saving...", className: "agent-releases-status--saving" },
    saved: {
      label: savedAtLabel ? `All changes saved - ${savedAtLabel}` : "All changes saved",
      className: "agent-releases-status--saved",
    },
    error: { label: "Couldn't save - will retry", className: "agent-releases-status--error" },
  }[saveState] || { label: "", className: "" };

  const publishEnabled = draftSubmitted && publishState !== "publishing" && Boolean(agentSummary?.release);
  const publishTitle = !draftSubmitted
    ? "Submit a draft first to enable Publish"
    : !agentSummary?.release
      ? "This agent has no live release yet - run the backfill first"
      : publishState === "publishing"
        ? "Publishing..."
        : "Ready to publish";

  const diffOptions = [
    { value: "live", label: "Live" },
    { value: "draft", label: "Draft (compiled over live)" },
    { value: "candidate", label: "Candidate (last submitted)" },
    ...releases.map((release) => ({ value: String(release.id), label: releaseOptionLabel(release) })),
  ];

  if (!PracticeId) {
    return (
      <div className="agent-releases-page p-4 text-light">
        Select or load a practice to view its agent releases.
      </div>
    );
  }

  return (
    <div className="agent-releases-page p-4">
      <div className="agent-releases-header">
        <h2 className="agent-releases-title">Agent Releases</h2>
        <p className="agent-releases-subtitle">
          Edit this agent&apos;s prompt as a draft, submit it for a demo call, then publish - every
          publish, settings change, and rollback becomes a new release you can always go back to.
          Separate from Agent Config and Prompt Hub, which still work and keep saving immediately.
        </p>
      </div>

      <div className="agent-releases-direction-toggle" role="tablist" aria-label="Direction">
        {["inbound", "outbound"].map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={direction === value}
            className={`agent-releases-direction-btn ${
              direction === value ? "agent-releases-direction-btn--active" : ""
            }`}
            onClick={() => setDirection(value)}
          >
            {value === "inbound" ? "Inbound" : "Outbound"}
          </button>
        ))}
      </div>

      {agentSummary ? (
        <div className="agent-releases-summary-bar">
          {agentSummary.release ? (
            <>
              <span className="agent-releases-summary-item">
                Live: <strong>{agentSummary.release.label}</strong> ({agentSummary.release.origin})
              </span>
              <span className="agent-releases-summary-item agent-releases-summary-digest">
                {agentSummary.release.digest}
              </span>
            </>
          ) : (
            <span className="agent-releases-summary-item agent-releases-summary-item--warning">
              No live release yet for this agent.
            </span>
          )}
        </div>
      ) : null}

      <div className="agent-releases-page-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activePageTab === "editor"}
          className={`agent-releases-page-tab ${
            activePageTab === "editor" ? "agent-releases-page-tab--active" : ""
          }`}
          onClick={() => setActivePageTab("editor")}
        >
          Editor
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activePageTab === "history"}
          className={`agent-releases-page-tab ${
            activePageTab === "history" ? "agent-releases-page-tab--active" : ""
          }`}
          onClick={handleOpenHistoryTab}
        >
          History{releases.length ? ` (${releases.length})` : ""}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activePageTab === "diff"}
          className={`agent-releases-page-tab ${
            activePageTab === "diff" ? "agent-releases-page-tab--active" : ""
          }`}
          onClick={handleOpenDiffTab}
        >
          Diff
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activePageTab === "settings"}
          className={`agent-releases-page-tab ${
            activePageTab === "settings" ? "agent-releases-page-tab--active" : ""
          }`}
          onClick={handleOpenSettingsTab}
        >
          Settings
        </button>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-light" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : loadErrorMessage ? (
        <div className="alert alert-danger py-2 mb-3">{loadErrorMessage}</div>
      ) : activePageTab === "editor" ? (
        <div className="agent-releases-card">
          <div className="agent-releases-card-header">
            <label className="agent-releases-label">Prompt draft</label>
            <span className={`agent-releases-status ${statusMeta.className}`}>{statusMeta.label}</span>
          </div>

          <textarea
            className="agent-releases-textarea"
            value={instructions.prompt}
            onChange={(event) => handlePromptChange(event.target.value)}
            placeholder="You are the front desk assistant for..."
            rows={14}
          />

          {saveErrorMessage ? <div className="alert alert-danger py-2 mt-3 mb-0">{saveErrorMessage}</div> : null}
          {submitErrorMessage ? (
            <div className="alert alert-danger py-2 mt-3 mb-0">{submitErrorMessage}</div>
          ) : null}

          <div className="agent-releases-actions">
            <button type="button" className="btn btn-primary" onClick={handleSaveNow} disabled={saveState === "saving"}>
              Save Draft
            </button>
            <button
              type="button"
              className="btn btn-outline-light"
              onClick={handleSubmitDraft}
              disabled={submitState === "submitting"}
            >
              {submitState === "submitting" ? "Submitting..." : "Submit Draft"}
            </button>
            <button
              type="button"
              className={`agent-releases-publish-btn ${
                publishEnabled ? "agent-releases-publish-btn--enabled" : "agent-releases-publish-btn--disabled"
              }`}
              onClick={handlePublish}
              disabled={!publishEnabled}
              title={publishTitle}
            >
              {publishState === "publishing" ? "Publishing..." : "Publish"}
            </button>
          </div>

          <div className="agent-releases-submit-hint">
            {!draftSubmitted
              ? "Submit a draft to enable Publish."
              : "Draft submitted - ready to publish."}
          </div>

          {publishMessage ? <div className="agent-releases-note">{publishMessage}</div> : null}
        </div>
      ) : activePageTab === "history" ? (
        <div className="agent-releases-card">
          <div className="agent-releases-card-header">
            <label className="agent-releases-label">Release history</label>
            {historyLoading ? (
              <span className="agent-releases-status agent-releases-status--saving">Loading...</span>
            ) : null}
          </div>

          {historyErrorMessage ? <div className="alert alert-danger py-2 mb-3">{historyErrorMessage}</div> : null}

          {!historyLoading && releases.length === 0 ? (
            <div className="agent-releases-empty">
              No releases yet for this agent. Publish a draft (or run the backfill) to start the
              history.
            </div>
          ) : (
            <ul className="agent-releases-history-list">
              {releases.map((release) => (
                <li key={release.id} className="agent-releases-history-item">
                  <div className="agent-releases-history-item-main">
                    <div className="agent-releases-history-item-heading">
                      <span className="agent-releases-history-version">{release.label}</span>
                      {agentSummary?.release?.id === release.id ? (
                        <span className="agent-releases-history-live-badge">LIVE</span>
                      ) : null}
                      <span className="agent-releases-history-origin">{release.origin}</span>
                    </div>
                    <div className="agent-releases-history-timestamp">
                      {formatTimestamp(release.createdAt) || release.createdAt}
                      {release.changeSummary ? ` - ${release.changeSummary}` : ""}
                    </div>
                    {release.changedParts?.length ? (
                      <div className="agent-releases-history-preview">
                        Changed: {release.changedParts.join(", ")}
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-light agent-releases-history-rollback-btn"
                    onClick={() => handleRequestRollback(release)}
                    disabled={agentSummary?.release?.id === release.id}
                    title={agentSummary?.release?.id === release.id ? "This is already live" : "Roll back to this release"}
                  >
                    Rollback
                  </button>
                </li>
              ))}
            </ul>
          )}

          {deployments.length > 0 ? (
            <>
              <label className="agent-releases-label agent-releases-label--spaced">Deployment history</label>
              <ul className="agent-releases-deployments-list">
                {deployments.map((deployment) => (
                  <li key={deployment.id} className="agent-releases-deployment-item">
                    <span className="agent-releases-deployment-action">{deployment.action}</span>
                    <span className="agent-releases-deployment-detail">
                      {deployment.fromReleaseId ? `from #${deployment.fromReleaseId} ` : ""}
                      to #{deployment.toReleaseId}
                    </span>
                    <span className="agent-releases-deployment-timestamp">
                      {formatTimestamp(deployment.createdAt) || deployment.createdAt}
                    </span>
                    {deployment.reason ? (
                      <span className="agent-releases-deployment-reason">&ldquo;{deployment.reason}&rdquo;</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : activePageTab === "diff" ? (
        <div className="agent-releases-card">
          <div className="agent-releases-card-header">
            <label className="agent-releases-label">Compare two releases</label>
          </div>

          <div className="agent-releases-diff-picker">
            <div className="agent-releases-field-col">
              <label className="agent-releases-label" htmlFor="agentReleasesDiffFrom">
                From
              </label>
              <select
                id="agentReleasesDiffFrom"
                className="agent-releases-input"
                value={diffFrom}
                onChange={(event) => setDiffFrom(event.target.value)}
              >
                {diffOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="agent-releases-field-col">
              <label className="agent-releases-label" htmlFor="agentReleasesDiffTo">
                To
              </label>
              <select
                id="agentReleasesDiffTo"
                className="agent-releases-input"
                value={diffTo}
                onChange={(event) => setDiffTo(event.target.value)}
              >
                {diffOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <button type="button" className="btn btn-primary" onClick={handleRunDiff} disabled={diffLoading}>
              {diffLoading ? "Comparing..." : "Compare"}
            </button>
          </div>

          {diffErrorMessage ? <div className="alert alert-danger py-2 mt-3 mb-0">{diffErrorMessage}</div> : null}

          {diffResult ? (
            <div className="agent-releases-diff-results">
              {Object.entries(diffResult.parts).map(([partName, partDiff]) => (
                <div className="agent-releases-diff-part" key={partName}>
                  <div className="agent-releases-diff-part-header">
                    <span className="agent-releases-diff-part-name">{partName}</span>
                    <span
                      className={`agent-releases-diff-part-status agent-releases-diff-part-status--${partDiff.status}`}
                    >
                      {partDiff.status}
                    </span>
                  </div>
                  {partDiff.status === "changed" ? (
                    <div className="agent-releases-diff-part-body">
                      {partDiff.changes.map((change) => (
                        <DiffPartPath change={change} key={change.path} />
                      ))}
                      {partDiff.textDiff?.prompt ? <DiffPromptLines lines={partDiff.textDiff.prompt} /> : null}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="agent-releases-card">
          <div className="agent-releases-settings-layout">
            <div className="agent-releases-settings-nav" role="tablist" aria-label="Settings part">
              {SETTINGS_PARTS.map((part) => (
                <button
                  key={part.key}
                  type="button"
                  role="tab"
                  aria-selected={settingsPartName === part.key}
                  className={`agent-releases-settings-nav-item ${
                    settingsPartName === part.key ? "agent-releases-settings-nav-item--active" : ""
                  }`}
                  onClick={() => handleSelectSettingsPart(part.key)}
                >
                  {part.label}
                </button>
              ))}
            </div>

            <div className="agent-releases-settings-body">
              {!agentSummary?.release ? (
                <div className="agent-releases-empty">
                  This agent has no live release yet - run the backfill before editing settings.
                </div>
              ) : !liveParts ? (
                <div className="d-flex justify-content-center py-4">
                  <div className="spinner-border text-light" role="status">
                    <span className="visually-hidden">Loading settings...</span>
                  </div>
                </div>
              ) : settingsPartName === "tools" ? (
                <ToolsReadOnlyList tools={liveParts.tools} />
              ) : !partDrafts[settingsPartName] ? null : (
                <>
                  {settingsPartName === "model" ? (
                    <ModelSettingsForm
                      value={partDrafts.model}
                      onChange={(next) => handlePartFieldChange("model", next)}
                    />
                  ) : settingsPartName === "voice" ? (
                    <VoiceSettingsForm
                      value={partDrafts.voice}
                      onChange={(next) => handlePartFieldChange("voice", next)}
                    />
                  ) : settingsPartName === "speech" ? (
                    <SpeechSettingsForm
                      value={partDrafts.speech}
                      onChange={(next) => handlePartFieldChange("speech", next)}
                    />
                  ) : settingsPartName === "behavior" ? (
                    <BehaviorSettingsForm
                      value={partDrafts.behavior}
                      onChange={(next) => handlePartFieldChange("behavior", next)}
                      direction={direction}
                    />
                  ) : settingsPartName === "postCall" ? (
                    <PostCallSettingsForm
                      value={partDrafts.postCall}
                      onChange={(next) => handlePartFieldChange("postCall", next)}
                    />
                  ) : null}

                  {partErrorMessage ? (
                    <div className="alert alert-danger py-2 mt-3 mb-0">{partErrorMessage}</div>
                  ) : null}

                  <div className="agent-releases-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => handleSavePart(settingsPartName)}
                      disabled={partSaveState === "saving"}
                    >
                      {partSaveState === "saving" ? "Saving..." : "Save changes"}
                    </button>
                  </div>
                  {partSaveState === "saved" ? (
                    <div className="agent-releases-note">Saved - a new release is now live.</div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {rollbackTargetRelease ? (
        <div className="agent-releases-backdrop" onClick={handleCancelRollback}>
          <div
            className="agent-releases-confirm-dialog"
            role="dialog"
            aria-label="Confirm rollback"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="agent-releases-modal-title">Roll back to {rollbackTargetRelease.label}?</h3>
            <p className="agent-releases-confirm-body">
              This moves the live route to {rollbackTargetRelease.label} (published{" "}
              {formatTimestamp(rollbackTargetRelease.createdAt) || rollbackTargetRelease.createdAt}). No
              release is created or deleted - the release you&apos;re rolling back from stays in
              history.
            </p>

            <label className="agent-releases-label" htmlFor="agentReleasesRollbackReason">
              Reason (required, shown in the deployment history)
            </label>
            <input
              id="agentReleasesRollbackReason"
              type="text"
              className="agent-releases-input"
              value={rollbackReason}
              onChange={(event) => setRollbackReason(event.target.value)}
              placeholder="e.g. the new prompt broke intake routing"
            />

            {rollbackErrorMessage ? <div className="alert alert-danger py-2 mt-3 mb-0">{rollbackErrorMessage}</div> : null}

            <div className="agent-releases-confirm-actions">
              <button
                type="button"
                className="btn btn-outline-light"
                onClick={handleCancelRollback}
                disabled={rollbackState === "rolling-back"}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmRollback}
                disabled={rollbackState === "rolling-back"}
              >
                {rollbackState === "rolling-back" ? "Rolling back..." : "Confirm rollback"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
